import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@material-ui/core';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import ErrorIcon from '@material-ui/icons/Error';
import EventIcon from '@material-ui/icons/Event';
import GetAppIcon from '@material-ui/icons/GetApp';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import LinearScaleIcon from '@material-ui/icons/LinearScale';
import PersonIcon from '@material-ui/icons/Person';
import ReplayIcon from '@material-ui/icons/Replay';
import ScheduleIcon from '@material-ui/icons/Schedule';
import SyncIcon from '@material-ui/icons/Sync';
import TimerIcon from '@material-ui/icons/Timer';

import { DialogoConfirmacao } from '../../Components';
import { api } from '../../services';
import { formatDateDDMMYYYYHHmm } from '../../utils';
import {
  podeBaixar,
  podeCancelar,
  podeReprocessar,
  useAcoesExecucao,
  usePermissoesRelatoriosFiscais,
} from './acoes';

// Etapa 5: dialog só de leitura (sem Reprocessar/Cancelar/Baixar — chegam na
// Etapa 7). Polling a cada 5s enquanto pendente/processando (plano §6.11) —
// react-admin v3 não tem refetchInterval, então é feito na unha com
// setInterval + api.get direto, limpando no unmount/fechamento do dialog.
const STATUS_EM_ANDAMENTO = ['pendente', 'processando'];

const CORES_STATUS = {
  pendente: '#9e9e9e',
  processando: '#1976d2',
  sucesso: '#2e7d32',
  sucesso_com_pendencias: '#ed6c02',
  erro: '#d32f2f',
  cancelada: '#616161',
};

const NOMES_STATUS = {
  pendente: 'Aguardando',
  processando: 'Processando',
  sucesso: 'Concluído',
  sucesso_com_pendencias: 'Concluído com pendências',
  erro: 'Erro',
  cancelada: 'Cancelada',
};

const NOMES_TIPO = {
  ingestao: 'Ingestão',
  pacote_mensal: 'Pacote mensal',
};

const NOMES_ORIGEM = {
  agendada: 'Agendada',
  manual: 'Manual',
  reprocessamento: 'Reprocessamento',
};

const NOMES_FONTE = {
  nfce: 'NFC-e',
  nfe: 'NF-e',
  nfe_contingencia: 'NF-e contingência',
  compra: 'Compras',
};

// problema = true: valor maior que zero é destacado como atenção.
const CAMPOS_METRICA = [
  {
    campo: 'arquivos',
    nome: 'Arquivos lidos',
    descricao:
      'XMLs encontrados nas pastas do período, antes de qualquer filtro.',
  },
  {
    campo: 'ja_existentes',
    nome: 'Já existentes',
    descricao:
      'Notas que já estavam gravadas de execuções anteriores e foram ignoradas.',
  },
  {
    campo: 'inseridas',
    nome: 'Inseridas',
    descricao: 'Notas novas gravadas agora no banco fiscal.',
  },
  {
    campo: 'substituidas',
    nome: 'Substituídas',
    descricao: 'Só no reprocessamento forçado: nota apagada e gravada de novo.',
  },
  {
    campo: 'pendencias_resolvidas',
    nome: 'Pendências resolvidas',
    descricao:
      'Pendências antigas que sumiram porque a nota agora foi gravada.',
  },
  {
    campo: 'sem_venda',
    nome: 'Sem venda',
    problema: true,
    descricao:
      'XML cuja chave não tem venda correspondente no banco. Não é gravada e vira pendência.',
  },
  {
    campo: 'sem_compra',
    nome: 'Sem compra',
    problema: true,
    descricao:
      'XML de compra cuja chave não tem compra no banco. Não é gravada e vira pendência.',
  },
  {
    campo: 'fora_periodo',
    nome: 'Fora do período',
    problema: true,
    descricao:
      'Data de emissão do XML fora do mês da pasta (com a tolerância configurada).',
  },
  {
    campo: 'erro_parse',
    nome: 'Erro de leitura do XML',
    problema: true,
    descricao: 'XML corrompido ou com formato que o worker não consegue ler.',
  },
  {
    campo: 'erro_insert',
    nome: 'Erro ao gravar',
    problema: true,
    descricao: 'O XML foi lido, mas o banco recusou a gravação.',
  },
  {
    campo: 'pastas_inexistentes',
    nome: 'Pastas inexistentes',
    problema: true,
    descricao: 'Dias em que a pasta de XML nem existia no servidor.',
  },
];

// Explica por que "Inseridas" ficou em 0 para uma fonte, somando o destino de
// cada arquivo lido.
function explicarSemInsercao(m) {
  if (!m || m.inseridas > 0) return null;
  if (!m.arquivos) {
    return m.pastas_inexistentes
      ? 'Nenhum XML foi lido: a pasta não existia em ao menos um dos dias.'
      : 'Nenhum XML foi encontrado nas pastas do período.';
  }

  const motivos = [
    [m.ja_existentes, 'já estavam gravadas'],
    [m.sem_venda, 'não têm venda correspondente no banco'],
    [m.sem_compra, 'não têm compra correspondente no banco'],
    [m.fora_periodo, 'estão com data de emissão fora do período'],
    [m.erro_parse, 'não puderam ser lidas (erro de parse)'],
    [m.erro_insert, 'falharam ao gravar'],
  ].filter(([valor]) => valor > 0);

  const explicado = motivos.reduce((soma, [valor]) => soma + valor, 0);
  const restante = m.arquivos - explicado;
  if (restante > 0) {
    motivos.push([
      restante,
      'foram ignoradas (problema já conhecido sem mudança no arquivo, ou nome de arquivo inválido)',
    ]);
  }

  return `Dos ${
    m.arquivos
  } arquivo(s) lido(s), nenhum foi inserido: ${motivos
    .map(([valor, texto]) => `${valor} ${texto}`)
    .join('; ')}.`;
}

const ICONES_ORIGEM = {
  agendada: <ScheduleIcon />,
  manual: <PersonIcon />,
  reprocessamento: <ReplayIcon />,
};

const COR_NIVEL = {
  erro: '#d32f2f',
  aviso: '#ed6c02',
  info: '#1976d2',
};

const CABECALHO_TABELA = {
  backgroundColor: '#f5f5f5',
  fontWeight: 700,
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: '#616161',
};

function humanizar(texto) {
  if (!texto) return '-';
  const limpo = String(texto).replace(/_/g, ' ');
  return limpo.charAt(0).toUpperCase() + limpo.slice(1);
}

function formatarDuracao(ms) {
  if (!ms) return '-';
  const segundos = Math.round(ms / 1000);
  if (segundos < 60) return `${segundos}s`;
  return `${Math.floor(segundos / 60)}min ${segundos % 60}s`;
}

function formatarData(data) {
  return data ? new Date(data).toLocaleDateString('pt-BR') : '-';
}

function descreverEscopo(execucao) {
  if (execucao.tipo === 'pacote_mensal') {
    return `Competência ${String(execucao.competencia_mes).padStart(2, '0')}/${
      execucao.competencia_ano
    }`;
  }
  if (execucao.data_inicio === execucao.data_fim) {
    return formatarData(execucao.data_inicio);
  }
  return `${formatarData(execucao.data_inicio)} a ${formatarData(
    execucao.data_fim,
  )}`;
}

function Legenda({ children }) {
  return (
    <Typography
      variant="body2"
      color="textSecondary"
      style={{ marginBottom: 12 }}
    >
      {children}
    </Typography>
  );
}

function Vazio({ children }) {
  return (
    <Typography
      color="textSecondary"
      style={{ padding: '24px 0', textAlign: 'center' }}
    >
      {children}
    </Typography>
  );
}

function Resumo({ titulo, valor, children, cor = '#455a64', icone }) {
  return (
    <Paper
      variant="outlined"
      style={{
        padding: '12px 14px',
        flex: '1 1 170px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        borderTop: `3px solid ${cor}`,
      }}
    >
      <span
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: `${cor}1f`,
          color: cor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icone}
      </span>
      <Box style={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="textSecondary" display="block">
          {titulo}
        </Typography>
        {children || (
          <Typography
            variant="subtitle1"
            style={{ fontWeight: 700, lineHeight: 1.3 }}
          >
            {valor}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box p={2}>{children}</Box>}
    </div>
  );
}

function useExecucaoFiscal(id, open, versao) {
  const [execucao, setExecucao] = useState(null);

  useEffect(() => {
    if (!open || !id) {
      setExecucao(null);
      return undefined;
    }

    let cancelado = false;

    async function buscar() {
      const response = await api.get(`/relatorios_fiscais_execucoes/${id}`);
      if (!cancelado) setExecucao(response.data);
    }

    buscar();

    return () => {
      cancelado = true;
    };
  }, [id, open, versao]);

  useEffect(() => {
    if (!open || !id) return undefined;
    if (!STATUS_EM_ANDAMENTO.includes(execucao?.status)) return undefined;

    const interval = setInterval(async () => {
      const response = await api.get(`/relatorios_fiscais_execucoes/${id}`);
      setExecucao(response.data);
    }, 5000);

    return () => clearInterval(interval);
  }, [id, open, execucao?.status]);

  return execucao;
}

function useOcorrenciasFiscais(id, open) {
  const [ocorrencias, setOcorrencias] = useState([]);

  useEffect(() => {
    if (!open || !id) {
      setOcorrencias([]);
      return undefined;
    }

    let cancelado = false;

    async function buscar() {
      const response = await api.get(
        `/relatorios_fiscais_ocorrencias?filter=${encodeURIComponent(
          JSON.stringify({ execucao_id: id }),
        )}&sort=${encodeURIComponent(
          JSON.stringify(['id', 'DESC']),
        )}&range=${encodeURIComponent(JSON.stringify([1, 200]))}`,
      );
      if (!cancelado) setOcorrencias(response.data.data || []);
    }

    buscar();

    return () => {
      cancelado = true;
    };
  }, [id, open]);

  return ocorrencias;
}

function IconeEtapa({ status }) {
  if (status === 'sucesso' || status === 'concluida')
    return <CheckCircleIcon style={{ color: '#2e7d32' }} fontSize="small" />;
  if (status === 'erro')
    return <ErrorIcon style={{ color: '#d32f2f' }} fontSize="small" />;
  if (status === 'processando' || status === 'em_andamento')
    return <SyncIcon style={{ color: '#1976d2' }} fontSize="small" />;
  return <HourglassEmptyIcon style={{ color: '#9e9e9e' }} fontSize="small" />;
}

function AbaEtapas({ execucao }) {
  const etapas = execucao.etapas || [];

  if (etapas.length === 0) {
    return <Vazio>Nenhuma etapa registrada ainda.</Vazio>;
  }

  return (
    <>
      <Legenda>
        Passos que o worker executou, na ordem, com o tempo de cada um.
      </Legenda>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={CABECALHO_TABELA} />
              <TableCell style={CABECALHO_TABELA}>Etapa</TableCell>
              <TableCell style={CABECALHO_TABELA}>Início</TableCell>
              <TableCell style={CABECALHO_TABELA}>Fim</TableCell>
              <TableCell style={CABECALHO_TABELA}>Duração</TableCell>
              <TableCell style={CABECALHO_TABELA}>Erro</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {etapas.map((etapa, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={`${etapa.codigo}-${index}`} hover>
                <TableCell padding="checkbox" align="center">
                  <Tooltip title={humanizar(etapa.status)}>
                    <span>
                      <IconeEtapa status={etapa.status} />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell style={{ fontWeight: 600 }}>
                  {humanizar(etapa.codigo)}
                </TableCell>
                <TableCell>
                  {etapa.inicio ? formatDateDDMMYYYYHHmm(etapa.inicio) : '-'}
                </TableCell>
                <TableCell>
                  {etapa.fim ? formatDateDDMMYYYYHHmm(etapa.fim) : '-'}
                </TableCell>
                <TableCell>{formatarDuracao(etapa.duracao_ms)}</TableCell>
                <TableCell style={{ color: '#d32f2f' }}>
                  {etapa.erro || '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </>
  );
}

function AbaMetricas({ execucao }) {
  const metricas = execucao.metricas || {};
  const fontes = Object.keys(metricas);

  if (fontes.length === 0) {
    return <Vazio>Sem resultados registrados ainda.</Vazio>;
  }

  const explicacoes = fontes
    .map((fonte) => [fonte, explicarSemInsercao(metricas[fonte])])
    .filter(([, texto]) => texto);

  return (
    <>
      <Legenda>
        O que aconteceu com os arquivos XML de cada fonte. Valores em destaque
        indicam problemas que geram pendências.
      </Legenda>

      {explicacoes.map(([fonte, texto]) => (
        <Paper
          key={fonte}
          variant="outlined"
          style={{
            marginBottom: 12,
            padding: '10px 14px',
            borderLeft: '4px solid #1976d2',
            backgroundColor: '#e3f2fd',
          }}
        >
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {NOMES_FONTE[fonte] || fonte}: nenhuma nota inserida
          </Typography>
          <Typography variant="body2">{texto}</Typography>
        </Paper>
      ))}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={CABECALHO_TABELA}>Resultado</TableCell>
              {fontes.map((fonte) => (
                <TableCell key={fonte} align="right" style={CABECALHO_TABELA}>
                  {NOMES_FONTE[fonte] || fonte}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {CAMPOS_METRICA.map(({ campo, nome, problema, descricao }) => (
              <TableRow key={campo} hover>
                <TableCell>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {nome}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    {descricao}
                  </Typography>
                </TableCell>
                {fontes.map((fonte) => {
                  const valor = metricas[fonte]?.[campo] ?? 0;
                  const destaque = problema && valor > 0;
                  return (
                    <TableCell
                      key={fonte}
                      align="right"
                      style={{
                        fontWeight: destaque ? 700 : 400,
                        color: destaque
                          ? '#d32f2f'
                          : valor === 0
                          ? '#bdbdbd'
                          : 'inherit',
                      }}
                    >
                      {valor}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </>
  );
}

function AbaOcorrencias({ id, open, ocorrencias }) {
  if (ocorrencias.length === 0) {
    return <Vazio>Nenhuma ocorrência registrada nesta execução.</Vazio>;
  }

  return (
    <>
      <Legenda>
        Avisos e erros registrados durante a execução (até 200, do mais recente
        para o mais antigo).
      </Legenda>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={CABECALHO_TABELA}>Nível</TableCell>
              <TableCell style={CABECALHO_TABELA}>Tipo</TableCell>
              <TableCell style={CABECALHO_TABELA}>Fonte</TableCell>
              <TableCell style={CABECALHO_TABELA}>Chave de acesso</TableCell>
              <TableCell style={CABECALHO_TABELA}>Mensagem</TableCell>
              <TableCell style={CABECALHO_TABELA}>Data</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ocorrencias.map((ocorrencia, index) => {
              const cor = COR_NIVEL[ocorrencia.nivel] || '#757575';
              return (
                <TableRow
                  key={ocorrencia.id}
                  hover
                  style={{
                    backgroundColor: index % 2 ? '#fafafa' : 'transparent',
                  }}
                >
                  <TableCell>
                    <Chip
                      size="small"
                      label={humanizar(ocorrencia.nivel)}
                      style={{
                        backgroundColor: `${cor}1f`,
                        color: cor,
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>{humanizar(ocorrencia.tipo)}</TableCell>
                  <TableCell>
                    {NOMES_FONTE[ocorrencia.fonte] || ocorrencia.fonte || '-'}
                  </TableCell>
                  <TableCell style={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {ocorrencia.chave_acesso || '-'}
                  </TableCell>
                  <TableCell>{ocorrencia.mensagem}</TableCell>
                  <TableCell>
                    {formatDateDDMMYYYYHHmm(ocorrencia.created_at)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </>
  );
}

function AbaResumo({ execucao }) {
  const resumo = execucao.resumo;

  if (!resumo) {
    return (
      <Vazio>
        {execucao.tipo === 'pacote_mensal'
          ? 'O resumo é gravado quando a geração do pacote termina.'
          : 'Resumo disponível apenas para pacotes mensais.'}
      </Vazio>
    );
  }

  return (
    <>
      <Legenda>Resumo bruto gerado pelo worker ao final do pacote.</Legenda>
      <pre
        style={{
          margin: 0,
          padding: 12,
          borderRadius: 4,
          backgroundColor: '#f5f5f5',
          whiteSpace: 'pre-wrap',
          fontFamily: 'monospace',
          fontSize: 12,
        }}
      >
        {JSON.stringify(resumo, null, 2)}
      </pre>
    </>
  );
}

const DialogoShow = forwardRef((props, ref) => {
  const [open, setOpen] = useState(false);
  const [id, setId] = useState(null);
  const [tab, setTab] = useState(0);

  const [versao, setVersao] = useState(0);
  const { podeGerar } = usePermissoesRelatoriosFiscais();

  const execucao = useExecucaoFiscal(id, open, versao);

  const {
    refDialogoConfirmacao,
    pedirReprocessar,
    pedirCancelar,
    handleConfirma,
    baixar,
  } = useAcoesExecucao({
    onAtualizado: () => setVersao((atual) => atual + 1),
    onCriada: (novoId) => {
      setId(novoId);
      setTab(0);
    },
  });

  useImperativeHandle(ref, () => ({
    handleOpen(execucaoId) {
      setId(execucaoId);
      setTab(0);
      setOpen(true);
    },
  }));

  function handleClose() {
    setOpen(false);
    setId(null);
  }

  const ocorrencias = useOcorrenciasFiscais(id, open);
  const segundosSemHeartbeat = execucao?.segundos_sem_heartbeat;
  const emAndamento = STATUS_EM_ANDAMENTO.includes(execucao?.status);
  const corStatus = CORES_STATUS[execucao?.status] || '#9e9e9e';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography style={{ paddingBottom: 8 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{ gap: 8 }}
        >
          <Box>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {execucao
                ? `${NOMES_TIPO[execucao.tipo] || execucao.tipo} #${id}`
                : `Execução #${id}`}
            </Typography>
            {execucao && (
              <Typography variant="body2" color="textSecondary">
                {descreverEscopo(execucao)}
              </Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            {execucao && (
              <Chip
                size="small"
                label={NOMES_STATUS[execucao.status] || execucao.status}
                style={{
                  backgroundColor: corStatus,
                  color: 'white',
                  fontWeight: 600,
                  marginRight: 8,
                }}
              />
            )}
            {execucao && podeBaixar(execucao) && (
              <Tooltip title="Baixar pacote (ZIP)">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => baixar(execucao)}
                >
                  <GetAppIcon />
                </IconButton>
              </Tooltip>
            )}
            {execucao && podeGerar && podeReprocessar(execucao) && (
              <Tooltip title="Reprocessar">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => pedirReprocessar(execucao)}
                >
                  <ReplayIcon />
                </IconButton>
              </Tooltip>
            )}
            {execucao && podeGerar && podeCancelar(execucao) && (
              <Tooltip title="Cancelar execução">
                <IconButton
                  size="small"
                  style={{ color: '#d32f2f' }}
                  onClick={() => pedirCancelar(execucao)}
                >
                  <CancelOutlinedIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Fechar">
              <IconButton size="small" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {execucao && (
          <>
            <Box
              display="flex"
              flexWrap="wrap"
              style={{ gap: 12, marginTop: 16 }}
            >
              <Resumo
                titulo="Iniciada em"
                cor="#1976d2"
                icone={<EventIcon />}
                valor={
                  execucao.iniciado_em
                    ? formatDateDDMMYYYYHHmm(execucao.iniciado_em)
                    : '-'
                }
              />
              <Resumo
                titulo="Duração"
                cor="#00897b"
                icone={<TimerIcon />}
                valor={formatarDuracao(execucao.duracao_ms)}
              />
              <Resumo
                titulo="Origem"
                cor="#7b1fa2"
                icone={ICONES_ORIGEM[execucao.origem] || <PersonIcon />}
                valor={NOMES_ORIGEM[execucao.origem] || execucao.origem}
              />
              <Resumo
                titulo={emAndamento ? 'Progresso' : 'Última etapa'}
                cor={corStatus}
                icone={emAndamento ? <SyncIcon /> : <LinearScaleIcon />}
              >
                {emAndamento ? (
                  <>
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                      {execucao.progresso_pct ?? 0}%
                      <Typography
                        component="span"
                        variant="caption"
                        color="textSecondary"
                      >
                        {' '}
                        {humanizar(execucao.etapa_atual)}
                      </Typography>
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={execucao.progresso_pct ?? 0}
                    />
                  </>
                ) : (
                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    {humanizar(execucao.etapa_atual)}
                  </Typography>
                )}
              </Resumo>
            </Box>

            {execucao.erro_mensagem && (
              <Paper
                variant="outlined"
                style={{
                  marginTop: 12,
                  padding: '10px 14px',
                  borderLeft: '4px solid #d32f2f',
                  backgroundColor: '#fdecea',
                }}
              >
                <Typography
                  variant="body2"
                  style={{ color: '#d32f2f', fontWeight: 600 }}
                >
                  A execução terminou com erro
                  {execucao.erro_codigo ? ` (${execucao.erro_codigo})` : ''}
                </Typography>
                <Typography variant="body2">
                  {execucao.erro_mensagem}
                </Typography>
              </Paper>
            )}

            {typeof segundosSemHeartbeat === 'number' &&
              segundosSemHeartbeat > 300 && (
                <Typography
                  variant="body2"
                  style={{ color: '#d32f2f', marginTop: 12 }}
                >
                  Sem sinal do worker há {Math.floor(segundosSemHeartbeat / 60)}{' '}
                  min.
                </Typography>
              )}
          </>
        )}
      </DialogTitle>

      <Tabs
        value={tab}
        onChange={(event, valor) => setTab(valor)}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="Etapas" />
        <Tab label="Resultado por fonte" />
        <Tab label={`Ocorrências (${ocorrencias.length})`} />
        <Tab label="Resumo" />
      </Tabs>

      <DialogContent dividers style={{ minHeight: 200 }}>
        {execucao ? (
          <>
            <TabPanel value={tab} index={0}>
              <AbaEtapas execucao={execucao} />
            </TabPanel>
            <TabPanel value={tab} index={1}>
              <AbaMetricas execucao={execucao} />
            </TabPanel>
            <TabPanel value={tab} index={2}>
              <AbaOcorrencias id={id} open={open} ocorrencias={ocorrencias} />
            </TabPanel>
            <TabPanel value={tab} index={3}>
              <AbaResumo execucao={execucao} />
            </TabPanel>
          </>
        ) : (
          <Vazio>Carregando...</Vazio>
        )}
      </DialogContent>

      <DialogoConfirmacao
        ref={refDialogoConfirmacao}
        handleConfirma={handleConfirma}
      />
    </Dialog>
  );
});

export default DialogoShow;
